import {useIntl} from 'react-intl';
import Hotkeys from 'react-hot-keys';
import {Alert} from '@material-ui/lab';
import {useSelector} from 'react-redux';
import {Toolbar, Box} from '@material-ui/core';
import {flatten, forEach, values} from 'lodash';
import React, {useContext, useEffect, useState, useCallback, useRef} from 'react';

import {getOriginUrl} from '../helpers';
import {Navigation} from '../Navigation';
import {AnnotatorView} from './AnnotatorView';
import {ImageThumbView} from './ImageThumbView';
import {PageListContext} from './PageListProvider';
import {PageListNavigation} from './PageListNavigation';
import {DATASET_SUB_OP as OP} from '../../../config/url';
import {useAppDispatch, RootState} from '../../../store';
import {mergeAnnotations} from '../../../store/dataPrepare';
import {BoxLabelProps} from '../../../base-components/StudioAnnotator/types';
import {getPredictions, startAutoLabel, stopAutoLabel} from '../../../store/iat';
import {BOX_LABEL_PREDICTION} from '../../../base-components/StudioAnnotator/const';
import {AnnotatorProvider} from '../../../base-components/StudioAnnotator/Provider';
import {DataPreparationOrigin} from '../../../types/dataset/DataPreparationContainer';
import {
  ImageType,
  CategoryType,
  AnnotationType,
  DataPrepareTypeStr,
} from '../../../types/dataset/DatasetGetAnnotationsResponse';

import './DetectionPrepare.scss';

export type DetectionAnnotationDictionary = {
  [imageId: string]: BoxLabelProps[];
};

const rawAnnotationsToDictionary = (
  rawAnnotations: AnnotationType[],
  labels: CategoryType[],
  isPrediction: boolean = false
) => {
  const dicctionary: DetectionAnnotationDictionary = {};

  rawAnnotations.forEach(element => {
    if (element.id !== null && element.id !== undefined) {
      if (!dicctionary[element.image_id]) {
        dicctionary[element.image_id] = [];
      }
      const box = element?.bbox || [];

      dicctionary[element.image_id].push({
        id: element.id.toString(),
        label: labels.find(label => label.id === element.category_id)?.name,
        x: box[0],
        y: box[1],
        width: box[2],
        height: box[3],
        style: isPrediction ? BOX_LABEL_PREDICTION : undefined,
      });
    }
  });

  return dicctionary;
};

export type DetectionPrepareProps = {
  projectId: string;
  datasetId?: string;
  disableBack: boolean;
  images?: ImageType[];
  labels?: CategoryType[];
  disableForward: boolean;
  origin: DataPreparationOrigin;
  annotations?: undefined | AnnotationType[];
  labelsMap?: {
    [key: string]: CategoryType;
  };
  onBack: () => void;
  onSelectionChange: (imageId: number) => void;
  onChange?: (imageId: number, annotations: BoxLabelProps[]) => void;
  onForward: (
    mergedPredictionAnnotations: AnnotationType[],
    discardedPredictionAnnotations: AnnotationType[]
  ) => void;
};

export const DetectionPrepare = ({
  images,
  origin,
  projectId,
  datasetId,
  labels = [],
  disableBack,
  disableForward,
  labelsMap = {},
  annotations: rawAnnotations,
  onBack,
  onChange = () => {},
  onForward = () => {},
  onSelectionChange = () => {},
}: DetectionPrepareProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [batchPredictionLoaded, setBatchPredictionLoaded] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [localProyectId, setLocalProjectId] = useState<string | undefined>(projectId);
  const {page, sortedList, listContent, itemsPerPageCount, setPage} = useContext(
    PageListContext
  );
  const autoLabelReady = useSelector((state: RootState) => state.iat.autoLabelReady);
  const isIATBusy = useSelector((state: RootState) => state.iat.isBusy);
  const isAutoLabel = useSelector((state: RootState) => state.dataPrepare.isAutoLabel);
  const annotationData = useSelector(
    (state: RootState) => state.dataPrepare.annotationData
  );
  const [annotations, setAnnotations] = useState<DetectionAnnotationDictionary>({});
  const [latestRawData, setLatestRawData] = useState<AnnotationType[] | undefined>();
  const [selectedImage, setSelectedImage] = useState<ImageType | null | undefined>(null);
  const intl = useIntl();

  const appDispatch = useAppDispatch();

  const hasPredictions = useCallback(
    (labels: BoxLabelProps[] | undefined | null) =>
      labels ? labels.some(element => !!element.style) : false,
    []
  );

  const getAutoLabels = useCallback(
    async (ids: number[]) => {
      const annotationCount = flatten(values(annotations)).length;
      if (ids.length && !!annotationCount && !isIATBusy) {
        await appDispatch(
          getPredictions({
            projectId,
            resourceId: datasetId,
            ids,
            samples: ids?.length,
          })
        ).then(({payload}) => {
          const data: AnnotationType[] = payload.body.annotations.map((element: any) => ({
            ...element,
            id: Math.floor(Math.random() * 100000),
          }));

          if (data && data.length) {
            setAnnotations({
              ...annotations,
              ...rawAnnotationsToDictionary(data, labels, true),
            });
          }

          if (ids.length > 1) {
            setBatchPredictionLoaded(true);
          }
        });
      }
    },
    [appDispatch, labels, isIATBusy, datasetId, projectId, annotations, setAnnotations]
  );

  const finalizeAnnotations = useCallback(
    (imageId: number | null | undefined) => {
      if (typeof imageId === 'number') {
        const imageAnnotations = annotations[imageId] ?? null;

        if (imageAnnotations && imageAnnotations.length) {
          if (hasPredictions(imageAnnotations)) {
            onChange(imageId, imageAnnotations);
          }

          const updatedAnnotations = imageAnnotations.map(element => ({
            ...element,
            style: undefined,
          }));

          setAnnotations({
            ...annotations,
            [imageId]: updatedAnnotations,
          });
        }
      }
    },
    [annotations, hasPredictions, onChange, setAnnotations]
  );

  const changeSelectedImage = useCallback(
    (index: number, finalize: boolean = true) => {
      const image = sortedList[index].data;

      if (image) {
        if (selectedImage?.id !== image.id && finalize) {
          finalizeAnnotations(selectedImage?.id);
        }

        setSelectedImage(image);
        setSelectedIndex(index);
        onSelectionChange(image.id);
      }
    },
    [
      sortedList,
      selectedImage,
      setSelectedImage,
      setSelectedIndex,
      onSelectionChange,
      finalizeAnnotations,
    ]
  );

  const verifyAnnotations = useCallback(async () => {
    const annotationsToMerge: AnnotationType[] = [];
    const imageIds: {id: number}[] = [];
    const ids: number[] = listContent.map(contentElement => contentElement.data.id);
    const newAnnotations: DetectionAnnotationDictionary = {};

    for (let i = 0; i < ids.length; i++) {
      const annotation = annotations[ids[i]];
      const entries: AnnotationType[] = [];

      (annotation || []).forEach(value => {
        if (value.style !== undefined) {
          entries.push({
            id: parseInt(value.id, 10),
            image_id: ids[i],
            category_id:
              value.label && labelsMap[value.label]
                ? Number(labelsMap[value.label].id)
                : undefined,
            bbox: [value.x, value.y, value.width, value.height],
          });
        }
      });

      if (entries.length) {
        annotationsToMerge.push(...entries);
        imageIds.push({id: ids[i]});
        newAnnotations[ids[i]] = annotation.map(element => ({
          ...element,
          style: undefined,
        }));
      }
    }

    await appDispatch(
      mergeAnnotations({
        resourceId: datasetId!,
        images: imageIds,
        annotations: annotationsToMerge,
      })
    );
    setAnnotations({
      ...annotations,
      ...newAnnotations,
    });
  }, [datasetId, labelsMap, annotations, listContent, appDispatch, setAnnotations]);

  const onPageChange = useCallback(
    async (page: number) => {
      setBatchPredictionLoaded(false);
      await verifyAnnotations();
      changeSelectedImage((page - 1) * itemsPerPageCount, false);
    },
    [verifyAnnotations, changeSelectedImage, itemsPerPageCount]
  );

  useEffect(
    function manageAutoLabelStarting() {
      if (projectId && datasetId && isAutoLabel && !autoLabelReady && !isIATBusy) {
        appDispatch(
          startAutoLabel({
            projectId,
            resourceId: datasetId,
            type: DataPrepareTypeStr.DETECTION,
          })
        );
      }
    },
    [projectId, datasetId, isIATBusy, isAutoLabel, autoLabelReady, appDispatch]
  );

  const autoLabelReadyRef = useRef(autoLabelReady);
  const annotationDataRef = useRef(annotationData);

  useEffect(() => {
    autoLabelReadyRef.current = autoLabelReady;
    annotationDataRef.current = annotationData;
  }, [autoLabelReady, annotationData]);

  useEffect(
    function manageAutoLabelShutdown() {
      return () => {
        if (autoLabelReadyRef.current && annotationDataRef.current) {
          appDispatch(stopAutoLabel({projectId, resourceId: datasetId}));
        }
      };
    },
    [projectId, datasetId, appDispatch]
  );

  useEffect(
    function setDefaultImageSelection() {
      if (sortedList.length) {
        if (selectedImage) {
          setSelectedIndex(
            sortedList.findIndex(item => item.data.id === selectedImage?.id)
          );
        } else {
          setSelectedImage(sortedList[0].data);
        }
      }
    },
    [sortedList, selectedImage, setSelectedImage]
  );

  useEffect(
    function updatePageBasedOnSelection() {
      if (page > 0 && selectedIndex >= 0 && itemsPerPageCount > 0) {
        const pageBySelectedIndex = Math.floor(selectedIndex / itemsPerPageCount) + 1;

        if (page !== pageBySelectedIndex) {
          setPage(pageBySelectedIndex);
        }
      }
    },
    [selectedIndex, page, itemsPerPageCount, setPage]
  );

  useEffect(
    function initializeProject() {
      if (projectId && localProyectId !== projectId) {
        setSelectedImage(null);
        setLocalProjectId(projectId);
      }

      return () => {
        setSelectedImage(null);
      };
    },
    [projectId, localProyectId]
  );

  useEffect(
    function loadData() {
      if (labels.length && rawAnnotations instanceof Array) {
        const newRawData = rawAnnotationsToDictionary(rawAnnotations, labels);

        if (JSON.stringify(newRawData) !== JSON.stringify(latestRawData)) {
          setAnnotations(newRawData);
          setLatestRawData(rawAnnotations);
        }
      }
    },
    [rawAnnotations, latestRawData, labels, setAnnotations]
  );

  useEffect(
    function fetchBulkPredictions() {
      const ids: number[] = listContent.map(contentElement => contentElement.data.id);
      if (
        autoLabelReady &&
        isAutoLabel &&
        listContent.length &&
        !batchPredictionLoaded &&
        selectedImage &&
        ids.includes(selectedImage.id)
      ) {
        const nIDs: number[] = ids.filter(id => {
          const imageAnnotations = annotations[id];
          return !imageAnnotations || !imageAnnotations.length;
        });

        getAutoLabels(nIDs);
      }
    },
    [
      autoLabelReady,
      isAutoLabel,
      listContent,
      batchPredictionLoaded,
      selectedImage,
      annotations,
      getAutoLabels,
    ]
  );

  useEffect(
    function fetchPredictionsForImage() {
      if (selectedImage && isAutoLabel && batchPredictionLoaded) {
        const imageAnnotations = annotations[selectedImage.id];

        if (!imageAnnotations || !imageAnnotations.length) {
          getAutoLabels([selectedImage.id]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedImage, isAutoLabel, batchPredictionLoaded, annotations]
  );

  useEffect(
    function notifyAnnotationChanges() {
      if (
        batchPredictionLoaded &&
        hasPredictions(selectedImage && annotations[selectedImage?.id])
      ) {
        if (selectedImage) {
          onChange(selectedImage.id, annotations[selectedImage.id]);
        }
      }
    },
    [batchPredictionLoaded, selectedImage, annotations, hasPredictions]
  );

  useEffect(
    function validateAnnotations() {
      const hasValidAnnotations = Object.values(annotations).some(val =>
        val.some(element => !!element.label)
      );

      setIsValid(hasValidAnnotations);
    },
    [annotations]
  );

  const onLocalChange = useCallback(
    (updatedAnnotations: BoxLabelProps[]) => {
      const newAnnotations = {...annotations};

      if (selectedImage) {
        const firstLabel = labels[0]?.name;
        const updatedAnnotationsWithDefaultLabel = updatedAnnotations.map(annotation => {
          let finalAnnotation = annotation;

          if (!finalAnnotation.label && firstLabel) {
            finalAnnotation = {...finalAnnotation, label: firstLabel};
          }

          return finalAnnotation;
        });

        newAnnotations[selectedImage.id] = updatedAnnotationsWithDefaultLabel;
        setAnnotations(newAnnotations);
        onChange(selectedImage.id, updatedAnnotationsWithDefaultLabel);
      }
    },
    [annotations, labels, onChange, selectedImage, setAnnotations]
  );

  const setSelection = useCallback(
    (image: ImageType) => {
      const index = sortedList?.findIndex(item => item.data.id === image?.id);

      if (index !== undefined) {
        changeSelectedImage(index);
      }
    },
    [changeSelectedImage, sortedList]
  );

  const prevImage = useCallback(
    () => changeSelectedImage(selectedIndex - 1 >= 0 ? selectedIndex - 1 : 0),
    [changeSelectedImage, selectedIndex]
  );

  const nextImage = useCallback(() => {
    const imageCount = sortedList?.length || 0;

    changeSelectedImage(
      selectedIndex + 1 < imageCount ? selectedIndex + 1 : imageCount - 1
    );
  }, [changeSelectedImage, selectedIndex, sortedList]);

  const onSelect = useCallback(
    (selections: BoxLabelProps[]) => {
      if (selections.length && selectedImage) {
        const ids: string[] = selections.map(element => element.id);

        setAnnotations({
          ...annotations,
          [selectedImage?.id]: annotations[selectedImage?.id].map(
            (element: BoxLabelProps) => {
              return ids.includes(element.id) ? {...element, style: undefined} : element;
            }
          ),
        });
      }
    },
    [annotations, selectedImage, setAnnotations]
  );

  const onSubmit = useCallback(() => {
    const mergedPredictionsAnnotations: AnnotationType[] = [];
    const discardedPredictionsAnnotations: AnnotationType[] = [];

    forEach(annotations, (boxLabelArr, imageId) => {
      boxLabelArr.forEach(boxLabel => {
        const annotation: AnnotationType = {
          id: Number(boxLabel.id),
          image_id: Number(imageId),
          category_id: boxLabel.label
            ? labels.find(label => label.name === boxLabel.label)?.id
            : undefined,
          bbox: [boxLabel.x, boxLabel.y, boxLabel.width, boxLabel.height],
        };
        mergedPredictionsAnnotations.push(annotation);
        if (!boxLabel.style) {
          discardedPredictionsAnnotations.push(annotation);
        }
      });
    });

    onForward(mergedPredictionsAnnotations, discardedPredictionsAnnotations);
  }, [annotations, labels, onForward]);

  const hotKey: {[key: string]: Function} = {
    right: nextImage,
    left: prevImage,
  };

  return (
    <>
      <Hotkeys keyName="right,left" onKeyUp={(keyName: string) => hotKey[keyName]()}>
        <div className="detection-view">
          <Toolbar
            className="data-prepare__header detection-view__header"
            variant="dense"
          >
            <Box className="detection-view__pagination">
              <PageListNavigation onChange={onPageChange} />
            </Box>
            <Box className="detection-view__container">
              {isAutoLabel && (
                <Alert severity="warning" className="detection-view__alert">
                  {intl.formatMessage({id: 'dataPrep.detection.assistedLabel'})}
                </Alert>
              )}
            </Box>
            <Navigation
              onForward={onSubmit}
              onBack={onBack}
              disableForward={disableForward || !isValid}
              disableBack={disableBack}
            />
          </Toolbar>
          <div className="detection-view__content">
            <AnnotatorProvider>
              <ImageThumbView
                images={images}
                origin={origin}
                projectId={projectId}
                onClick={setSelection}
                selection={selectedImage}
                annotations={annotations}
              />
              <AnnotatorView
                image={selectedImage}
                annotations={selectedImage ? annotations[selectedImage.id] : undefined}
                src={
                  selectedImage && sortedList?.length
                    ? `${getOriginUrl(origin)(projectId, OP.GET_BY_NAME)}?name=${
                        selectedImage?.file_name
                      }`
                    : ''
                }
                labels={labels.map(element => ({
                  id: element.id,
                  label: element.name,
                }))}
                onSelect={onSelect}
                onChange={onLocalChange}
              />
            </AnnotatorProvider>
          </div>
        </div>
      </Hotkeys>
    </>
  );
};
